import { useState } from 'react'

import {
  Box,
  Icon,
  Spinner,
  Table,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { MapColumnsStepDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { usePrompt } from 'modules/core/lib/react-router'

import { useEmployeeImporterFields } from 'modules/employees/hooks/useEmployeeImporterFields'
import { useGetImporterProcessFileContent } from 'modules/employees/hooks/useGetImporterProcessFileContent'
import { WizardData } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/WizardData'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

import MapColumnsInfo from './components/MapColumnsInfo'
import MapSelector from './components/MapSelector'

const INITIAL_VISIBLE_ROWS = 4

const isCompletedStep = (_data: WizardData) => true

const MapColumnsStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [showMissingFieldsText, setShowMissingFieldsText] = useState<boolean>(false)
  const [showAllRows, setShowAllRows] = useState<boolean>(false)
  const { loading: loadingFileContent, fileContent } = useGetImporterProcessFileContent({
    importerProcessUuid: data.importerProcessUuid,
  })
  const { loading: loadingFields, fields } = useEmployeeImporterFields()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { loading: loadingIntegrations, data: mapColumnsStepData } = useQuery(
    MapColumnsStepDocument,
    { variables: { companyId } }
  )
  const companyIntegrationNodesSet = useUnsafeMemo(() => {
    const companyIntegrationNodes =
      mapColumnsStepData?.marketplace?.companyInstallationsConnection?.nodes || []
    return new Set(companyIntegrationNodes.map((node) => node.integrationDatoId))
  }, [mapColumnsStepData?.marketplace?.companyInstallationsConnection?.nodes])
  const companyIntegrations = useUnsafeMemo(() => {
    return Object.entries(integrationCodes).map(([integrationName, integrationCode]) => ({
      integrationName: integrationName,
      hasIntegration: companyIntegrationNodesSet.has(integrationCode),
    }))
  }, [companyIntegrationNodesSet])

  const filteredFields = useUnsafeMemo(() => {
    return fields?.filter((field) => {
      if (field.fieldName === 'a3_innuva_code') {
        return companyIntegrations.find((ci) => ci.integrationName === 'a3innuva')?.hasIntegration
      }

      if (field.fieldName === 'a3_nom_code') {
        return companyIntegrations.find((ci) => ci.integrationName === 'a3nom')?.hasIntegration
      }

      return true
    })
  }, [fields, companyIntegrations])

  const mandatoryFields = useUnsafeMemo(
    () => filteredFields?.filter((field) => field.required),
    [filteredFields]
  )
  const missingMandatoryFieldsMapped = useUnsafeMemo(() => {
    return mandatoryFields?.filter(
      (field) => !data.mappedFields?.some((x) => x.fieldName === field.fieldName)
    )
  }, [mandatoryFields, data.mappedFields])
  const missingMandatoryFieldsMappedNames = useUnsafeMemo(() => {
    return missingMandatoryFieldsMapped?.map((x) => x.label).join(', ')
  }, [missingMandatoryFieldsMapped])

  useUnsafeMemo(() => {
    if (loadingFileContent) return
    if (!fileContent) return
    if (data.originalFileContent) return

    onDataChanged({
      ...data,
      originalFileContent: fileContent,
      editedFileContent: fileContent,
    })
  }, [data, fileContent, loadingFileContent, onDataChanged])

  useOnNext(() => {
    const hasMissingMandatoryFields = !!missingMandatoryFieldsMapped?.length

    setShowMissingFieldsText(hasMissingMandatoryFields)

    if (!hasMissingMandatoryFields) {
      const mappedFieldsNames = data.mappedFields.map((x) => x.fieldName)
      track(trackingEvents.importEmployees.mapColumnsStep.next, { mappedFields: mappedFieldsNames })
    }

    if (hasMissingMandatoryFields) {
      throw new Error()
    }
  })

  usePrompt(i18n.t('common.confirm_navigation'))

  const onFieldSelect = (fieldName?: string, columnIndex?: number) => {
    const mappedId = data.mappedFields?.findIndex((x) => x.columnIndex === columnIndex)
    const newMappedFields = [...data.mappedFields]
    const field = fields?.find((f) => f.fieldName === fieldName)
    if (!fieldName) {
      newMappedFields.splice(mappedId, 1)
    } else if (mappedId !== -1) {
      newMappedFields[mappedId] = {
        fieldName: field?.fieldName,
        fieldType: field?.fieldType,
        columnIndex: columnIndex,
      }
    } else {
      newMappedFields.push({
        columnIndex: columnIndex,
        fieldType: field?.fieldType,
        fieldName: fields?.find((f) => f.fieldName === fieldName)?.fieldName,
      })
    }

    onDataChanged({
      ...data,
      mappedFields: newMappedFields,
    })
  }

  const selectedField = (index: number) => {
    const mappedField = data.mappedFields.find((x) => x.columnIndex === index)

    return fields?.find((x) => x.fieldName === mappedField?.fieldName)?.fieldName
  }

  const visibleRows = showAllRows
    ? fileContent?.rows
    : fileContent?.rows?.slice(0, INITIAL_VISIBLE_ROWS)
  const hiddenRowsCount = fileContent?.rows?.slice(INITIAL_VISIBLE_ROWS).length

  const tableOptionsBottom = () => {
    if (!!fileContent.rows && fileContent.rows.length < INITIAL_VISIBLE_ROWS) return null

    return (
      !!hiddenRowsCount && (
        <Box
          onClick={() => setShowAllRows(!showAllRows)}
          flexDirection="row"
          width="full"
          gap="s8"
          cursor="pointer"
        >
          <Icon.Small icon={showAllRows ? 'arrow-up' : 'arrow-down'} color="grey700" />
          <Text.Normal color="grey700" size="100">
            {showAllRows
              ? i18n.tp('importer_process.map_columns_step.hide', { count: hiddenRowsCount })
              : i18n.tp('importer_process.map_columns_step.load_more', {
                  count: hiddenRowsCount,
                })}
          </Text.Normal>
        </Box>
      )
    )
  }

  if (loadingFileContent || loadingFields || loadingIntegrations) {
    return (
      <Box justifyContent="center" alignItems="center" paddingY="s24">
        <Spinner />
      </Box>
    )
  }

  return (
    <Box flexDirection="column" gap="s24" paddingRight="s8">
      <Box>
        <Box flexDirection="column" gap="s8">
          <Text.Header2>{i18n.t('importer_process.map_columns_step.header')}</Text.Header2>
          <Text.Normal>
            {i18n.t('importer_process.map_columns_step.header_description')}
          </Text.Normal>
        </Box>
        <MapColumnsInfo
          fields={filteredFields || []}
          mappedFields={data.mappedFields}
          isWarning={showMissingFieldsText && !!missingMandatoryFieldsMapped?.length}
          missingFieldNames={missingMandatoryFieldsMappedNames || ''}
        />
      </Box>
      <Box paddingBottom="s8" position="relative">
        <Table
          hideBorder
          optionsBottom={() => tableOptionsBottom()}
          style={{ position: 'absolute' }}
        >
          <Table.Head>
            <Table.Row background="lighter920">
              {fileContent.headers?.map((name, index) => (
                <Table.HeadCell
                  key={`${index}-${name}`}
                  paddingX="s2"
                  background="lighter920"
                  width={240}
                >
                  <MapSelector
                    key={`${index}-${name}`}
                    selectedValue={selectedField(index)}
                    fields={filteredFields || []}
                    index={index}
                    onFieldSelect={onFieldSelect}
                    mappedFields={data.mappedFields}
                  />
                </Table.HeadCell>
              ))}
            </Table.Row>
            <Table.Row>
              {fileContent?.headers?.map((columnName, index) => (
                <Table.HeadCell key={`${index}-${columnName}`} ellipsis>
                  {columnName}
                </Table.HeadCell>
              ))}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {visibleRows?.map((row, rowIndex) => (
              <Table.Row key={rowIndex}>
                {row?.map((cell, index) => (
                  <Table.Cell key={`${index}-${cell}`}>
                    <Text.Normal ellipsis>{cell}</Text.Normal>
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Box>
    </Box>
  )
}

export const mapColumnsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('importer_process.map_columns_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <MapColumnsStep {...props} />,
  isCompleted: (data) => isCompletedStep(data),
})
